import React, { useState, useEffect } from "react";
import { PlayerRole } from "../../context/GameContext";

interface RoleRevealOverlayProps {
  role: PlayerRole;
  onComplete: () => void;
}

const RoleRevealOverlay: React.FC<RoleRevealOverlayProps> = ({
  role,
  onComplete,
}) => {
  const [visible, setVisible] = useState(true);

  // Check if this is a special role that needs a reveal
  const isSpecialRole = [
    PlayerRole.MAFIA,
    PlayerRole.DOCTOR,
    PlayerRole.DETECTIVE,
  ].includes(role);

  // Determine which image to show based on role
  const getImageForRole = () => {
    switch (role) {
      case PlayerRole.MAFIA:
        return "/assets/images/Mafia1.png";
      case PlayerRole.DOCTOR:
        return "/assets/images/DoctorSaveMe.png";
      case PlayerRole.DETECTIVE:
        return "/assets/images/detective.png";
      default:
        return "/assets/images/VillagersDiscussion.png";
    }
  };

  // Main effect that handles both cases
  useEffect(() => {
    if (!isSpecialRole) {
      // Skip straight to game for non-special roles
      onComplete();
      return;
    }

    // For special roles, show the overlay for 5 seconds
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onComplete, 500); // Allow time for fade-out animation
    }, 5000);

    return () => clearTimeout(timer);
  }, [onComplete, isSpecialRole]);

  const imageSrc = getImageForRole();

  // Don't render anything for non-special roles
  if (!isSpecialRole || !imageSrc) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-90 flex flex-col items-center justify-center z-50 transition-opacity duration-500 ${
        visible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div className="max-w-md mx-auto p-4 text-center">
        <h2 className="text-2xl font-bold mb-4 text-white">
          You are the {role.charAt(0).toUpperCase() + role.slice(1)}!
        </h2>

        <div className="mb-6">
          <img
            src={imageSrc}
            alt={`${role} role`}
            className="mx-auto max-h-96 rounded shadow-lg"
          />
        </div>

        <p className="text-indigo-300 mb-6">
          {role === PlayerRole.MAFIA &&
            "Eliminate the villagers without being caught."}
          {role === PlayerRole.DOCTOR &&
            "Save villagers from the mafia's attacks."}
          {role === PlayerRole.DETECTIVE &&
            "Investigate players to find the mafia."}
        </p>

        <button
          onClick={() => {
            setVisible(false);
            setTimeout(onComplete, 500);
          }}
          className="btn btn-primary"
        >
          Continue to Game
        </button>
      </div>
    </div>
  );
};

export default RoleRevealOverlay;
